import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { t } from '../i18n'
import { useUi } from '../State/Ui'
import { ChevronLeft, ChevronRight } from './Icons'

/* A row of chips that scrolls sideways (spec 5.1). On desktop: fades and ‹ ›
   buttons that appear only when there's more on that side, a click moves by
   the visible width minus ~140 px. On mobile: no buttons, a fade on the right
   and the last visible chip cut in half. The selected chip is kept in view. */
export default function ChipRow({ children, label, activeKey, className = '' }) {
  const { isMobile } = useUi()
  const ref = useRef(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const left = el.scrollLeft > 2
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2
    setEdges((e) => (e.left === left && e.right === right ? e : { left, right }))
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    measure()
    el.addEventListener('scroll', measure, { passive: true })
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      el.removeEventListener('scroll', measure)
      ro?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  // Keep the highlighted (just added) or selected chip in view.
  useLayoutEffect(() => {
    measure()
    const el = ref.current
    const target =
      el?.querySelector('[data-highlight="true"]') ||
      el?.querySelector('[aria-pressed="true"], [aria-selected="true"]')
    if (!el || !target) return
    const left = target.offsetLeft - el.offsetLeft
    if (left < el.scrollLeft) el.scrollTo({ left: Math.max(0, left - 16), behavior: 'smooth' })
    else if (left + target.offsetWidth > el.scrollLeft + el.clientWidth) {
      el.scrollTo({ left: left + target.offsetWidth - el.clientWidth + 16, behavior: 'smooth' })
    }
  }, [activeKey, measure])

  const scrollBy = (direction) => {
    const el = ref.current
    if (!el) return
    el.scrollBy({ left: direction * Math.max(el.clientWidth - 140, 120), behavior: 'smooth' })
  }

  return (
    <div
      className={`chip-row ${className} ${edges.left ? 'fade-left' : ''} ${
        edges.right ? 'fade-right' : ''
      }`}
    >
      <div className="chip-scroller" ref={ref} role="group" aria-label={label}>
        {children}
      </div>
      {!isMobile && edges.left && (
        <button
          type="button"
          className="chip-arrow chip-arrow-left"
          aria-label={t('chips.previous')}
          onClick={() => scrollBy(-1)}
        >
          <ChevronLeft size={18} strokeWidth={2.5} />
        </button>
      )}
      {!isMobile && edges.right && (
        <button
          type="button"
          className="chip-arrow chip-arrow-right"
          aria-label={t('chips.next')}
          onClick={() => scrollBy(1)}
        >
          <ChevronRight size={18} strokeWidth={2.5} />
        </button>
      )}
    </div>
  )
}
